import { useState, useMemo, useEffect } from 'react';
import { usePermissions } from '../../hooks/usePermissions';
import { type ColumnDef } from '@tanstack/react-table';
import { Plus, Pencil, LayoutGrid, List, Search, PowerOff, RotateCcw, Radio } from 'lucide-react';
import { toast } from 'sonner';
import { useCrud } from '../../api/hooks/useCrud';
import { DataTable, PaginationFooter } from '../../components/ui/DataTable';
import { Modal } from '../../components/ui/Modal';
import { ConfirmModal } from '../../components/ui/ConfirmModal';
import { SetupPageHero } from '../../components/onboarding/SetupPageHero';
import { api } from '../../api/client';
import { useAuthStore } from '../../store/authStore';
import type { StatusDispositivo, TipoDispositivoLeitura } from '../../types/models';
import { RegistroMeta } from '../../components/admin/registro-meta';
import { DispositivosFormModal } from '../../components/forms/DispositivosFormModal';
import { TIPO_LABEL, TIPO_LEITOR, STATUS_LABEL } from './dispositivos-shared';
import '../../components/ui/datatable.css';
import './dispositivos-modal.css';
import { ROUTES } from '../../api/routes';

/** Linha listada — espelha `DispositivoLeitura` na API (leitores / pontos de acesso). */
interface DispositivoLeituraRow {
  id: string;
  nome: string;
  codigo: string;
  tipo: TipoDispositivoLeitura;
  status: StatusDispositivo;
  ativo: boolean;
  criadoEm?: string;
  atualizadoEm?: string;
  ultimoHeartbeatEm?: string;
  localInstalacao?: string;
  _count?: { eventosAcesso?: number; incidentes?: number };
}

function situacaoBadge(status: StatusDispositivo, ativo: boolean) {
  if (!ativo) {
    return <span className="badge badge-inactive">Cadastro inativo</span>;
  }
  const colors: Partial<Record<StatusDispositivo, string>> = {
    ONLINE: '#059669',
    OFFLINE: '#64748b',
    MANUTENCAO: '#d97706',
    FALHA: '#dc2626',
    INATIVO: '#64748b',
  };
  const c = colors[status] || '#64748b';
  const label = STATUS_LABEL[status] || status;
  return (
    <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: '.72rem', fontWeight: 600, background: `${c}18`, color: c }}>
      {label}
    </span>
  );
}

function parseApiError(err: unknown, fallback: string): string {
  if (typeof err === 'object' && err !== null && 'response' in err) {
    const data = (err as { response?: { data?: { message?: unknown } } }).response?.data;
    const m = data?.message;
    if (typeof m === 'string') return m;
    if (Array.isArray(m)) return m.filter(Boolean).join(', ');
  }
  return fallback;
}

export function DispositivosPage() {
  const { canManage, canView } = usePermissions();

  const escolaId = useAuthStore((s) => s.user?.escolaId);
  const { data: dispositivos, isLoading, error: loadError, create, update, deactivate } = useCrud<DispositivoLeituraRow>('dispositivos-leitura');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<DispositivoLeituraRow | null>(null);
  const [form, setForm] = useState({
    nome: '',
    codigo: '',
    tipo: 'RFID' as TipoDispositivoLeitura,
    localInstalacao: '',
  });
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterTipo, setFilterTipo] = useState('');
  const [viewMode, setViewMode] = useState<'lista' | 'cards'>('lista');
  const [pageSize, setPageSize] = useState(10);

  const openNew = () => {
    setEditing(null);
    setForm({ nome: '', codigo: '', tipo: 'RFID', localInstalacao: '' });
    setModalOpen(true);
  };

  const openEdit = (t: DispositivoLeituraRow) => {
    setEditing(t);
    setForm({
      nome: t.nome,
      codigo: t.codigo,
      tipo: t.tipo,
      localInstalacao: t.localInstalacao || '',
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    const nome = form.nome.trim();
    const codigo = form.codigo.trim();
    if (!nome || !codigo) {
      toast.error('Informe o nome e o código do dispositivo.');
      return;
    }
    const payload = {
      nome,
      codigo,
      tipo: form.tipo,
      localInstalacao: form.localInstalacao.trim() || undefined,
    };
    try {
      if (editing) {
        await update.mutateAsync({ id: editing.id, ...payload });
        toast.success('Dispositivo atualizado!');
      } else {
        await create.mutateAsync(payload);
        toast.success('Dispositivo cadastrado!');
      }
      setModalOpen(false);
    } catch (err) {
      toast.error(parseApiError(err, 'Erro ao salvar dispositivo'));
    }
  };

  const requestDelete = (id: string) => setDeleteId(id);
  const confirmDelete = async () => {
    if (!deleteId) return;
    const id = deleteId;
    setDeleteId(null);
    const dispositivo = (dispositivos ?? []).find(d => d.id === id);
    if (!dispositivo) return;

    try {
      await deactivate.mutateAsync(id);
      toast.success('Dispositivo inativado');
    } catch (err) {
      toast.error(parseApiError(err, 'Erro ao inativar'));
    }
  };

  const handleAtivar = async (id: string) => {
    try {
      await api.patch(ROUTES.dispositivosLeitura.remover(escolaId!, id), { ativo: true });
      toast.success('Dispositivo reativado!');
      window.location.reload();
    } catch (e: unknown) {
      toast.error(parseApiError(e, 'Erro ao reativar dispositivo'));
    }
  };

  const filtered = useMemo(() => {
    let list = dispositivos;
    if (filterTipo) list = list.filter((t) => t.tipo === filterTipo);
    const q = searchQuery.trim().toLowerCase();
    if (!q) return list;
    return list.filter((t) => {
      const tipoTxt = TIPO_LABEL[t.tipo] || t.tipo;
      return [t.nome, t.codigo, tipoTxt].join(' ').toLowerCase().includes(q);
    });
  }, [dispositivos, filterTipo, searchQuery]);

  useEffect(() => {
    if (loadError) {
      toast.error('Não foi possível carregar os dispositivos. Tente atualizar a página.');
    }
  }, [loadError]);

  const ativosOperacao = dispositivos.filter((t) => t.ativo && t.status === 'ONLINE').length;

  const columns: ColumnDef<DispositivoLeituraRow>[] = [
    {
      accessorKey: 'nome',
      header: 'Nome',
      cell: ({ row }) => (
        <div style={{ fontWeight: 600, color: 'var(--text-main)', minWidth: 0 }}>{row.original.nome}</div>
      ),
    },
    {
      accessorKey: 'codigo',
      header: 'Código',
      cell: ({ row }) => (
        <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: '.82rem' }}>{row.original.codigo}</span>
      ),
    },
    {
      accessorKey: 'tipo',
      header: 'Tipo',
      cell: ({ row }) => (
        <span className="badge badge-active">{TIPO_LABEL[row.original.tipo] || row.original.tipo}</span>
      ),
    },
    {
      id: 'situacao',
      header: 'Situação',
      cell: ({ row }) => situacaoBadge(row.original.status, row.original.ativo),
    },
    {
      id: 'registro',
      header: 'Registro',
      cell: ({ row }) => {
        const d = row.original;
        if (!d.criadoEm) return <span className="ac-resp-registro ac-resp-registro--empty">—</span>;
        return <RegistroMeta criadoEm={d.criadoEm} atualizadoEm={d.atualizadoEm} />;
      },
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        const d = row.original;
        return (
          <div style={{ display: 'flex', gap: 4, opacity: d.ativo ? 1 : 0.4 }}>
            {d.ativo && (
              <button type="button" className="action-btn" title="Editar" onClick={() => openEdit(d)}>
                <Pencil size={15} />
              </button>
            )}
            {!d.ativo && (
              <button type="button" className="action-btn" style={{ color: 'var(--success-color, #10b981)' }} title="Reativar" onClick={() => handleAtivar(d.id)}>
                <RotateCcw size={15} />
              </button>
            )}
            {d.ativo && (
              <button type="button" className="action-btn action-btn-danger" style={{ color: 'var(--danger-color, #ef4444)' }} title="Desativar" onClick={() => requestDelete(d.id)}>
                <PowerOff size={15} />
              </button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="admin-page" style={{ marginTop: -24, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      {!isLoading && (
        <SetupPageHero
          icon={Radio}
          etapa={8}
          totalEtapas={8}
          titulo="Dispositivos de leitura"
          descricao={
            dispositivos.length === 0
              ? 'Cadastre leitores RFID, catracas ou pontos de registro (QR, manual, app). Cada item precisa de um nome e um código único na rede.'
              : 'Gerencie os pontos de leitura da escola. Acompanhe o estado operacional (online/offline) e mantenha códigos e tipos atualizados.'
          }
          acoes={[]}
          dismissivel={false}
          compact
        />
      )}
      <div className="ac-admin-data-card" style={{ background: 'var(--bg-card, white)', border: '1px solid var(--border-color, #e5e7eb)', borderRadius: 12, overflow: 'hidden', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', borderBottom: '1px solid var(--border-color, #e5e7eb)', background: 'var(--bg-main, #f8fafc)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <div style={{ position: 'relative' }}>
              <Search size={14} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
              <input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar por nome ou código..."
                title="Buscar por nome ou código"
                style={{ height: 34, width: 280, borderRadius: 8, border: '1px solid var(--border-color, #e5e7eb)', padding: '0 10px 0 30px', fontSize: '.82rem', background: 'var(--bg-card)', color: 'var(--text-main)' }}
              />
            </div>
            <select
              value={filterTipo}
              onChange={(e) => setFilterTipo(e.target.value)}
              title="Filtrar por tipo"
              style={{ height: 34, borderRadius: 8, border: '1px solid var(--border-color, #e5e7eb)', padding: '0 12px', fontSize: '.82rem', background: 'var(--bg-card)', color: 'var(--text-main)', flexShrink: 0 }}
            >
              <option value="">Todos os tipos</option>
              {TIPO_LEITOR.map(({ value, label }) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
            <button type="button" className="btn-primary" onClick={openNew} style={{ height: 34, padding: '0 12px', fontSize: '.8rem' }}>
              <Plus size={15} /> Novo dispositivo
            </button>
            {ativosOperacao > 0 && (
              <span style={{ fontSize: '.76rem', color: '#059669', whiteSpace: 'nowrap', flexShrink: 0, fontWeight: 600 }}>{ativosOperacao} online</span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>{filtered.length} registro(s)</span>
            <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}
              style={{ height: 32, borderRadius: 8, border: '1px solid var(--border-color, #e5e7eb)', padding: '0 8px', fontSize: '.78rem', background: 'var(--bg-card)', color: 'var(--text-main)' }}>
              {[5, 10, 20, 40].map((n) => <option key={n} value={n}>{n}/página</option>)}
            </select>
            <div style={{ display: 'flex', border: '1px solid var(--border-color, #e5e7eb)', borderRadius: 8, overflow: 'hidden' }}>
              <button type="button" onClick={() => setViewMode('lista')} title="Visualização em lista" style={{ height: 32, width: 36, border: 'none', background: viewMode === 'lista' ? 'var(--bg-main, #eef2ff)' : 'var(--bg-card)', color: viewMode === 'lista' ? '#4f46e5' : 'var(--text-muted)', cursor: 'pointer' }}><List size={14} style={{ margin: '0 auto' }} /></button>
              <button type="button" onClick={() => setViewMode('cards')} title="Visualização em cards" style={{ height: 32, width: 36, border: 'none', borderLeft: '1px solid var(--border-color, #e5e7eb)', background: viewMode === 'cards' ? 'var(--bg-main, #eef2ff)' : 'var(--bg-card)', color: viewMode === 'cards' ? '#4f46e5' : 'var(--text-muted)', cursor: 'pointer' }}><LayoutGrid size={14} style={{ margin: '0 auto' }} /></button>
            </div>
          </div>
        </div>
        <div className="ac-admin-list-scroll" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflowY: 'auto', minHeight: 0 }}>
          {viewMode === 'lista' ? (
            <DataTable columns={columns} data={filtered} isLoading={isLoading} searchPlaceholder="Buscar…" showToolbar={false} pageSize={pageSize} />
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12, padding: 12 }}>
              {filtered.map((t) => (
                <div key={t.id} style={{ border: '1px solid var(--border-color, #e5e7eb)', borderRadius: 10, padding: 12, background: 'var(--bg-card)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, gap: 8, alignItems: 'flex-start' }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 700, color: 'var(--text-main)', fontSize: '.9rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.nome}</div>
                      <div style={{ fontFamily: 'ui-monospace, monospace', fontSize: '.82rem', color: 'var(--text-muted)', marginTop: 2, wordBreak: 'break-all' }}>{t.codigo}</div>
                      <div style={{ marginTop: 6 }}>
                        <span className="badge badge-active">{TIPO_LABEL[t.tipo] || t.tipo}</span>
                      </div>
                    </div>
                    {situacaoBadge(t.status, t.ativo)}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 4, opacity: t.ativo ? 1 : 0.4 }}>
                    {t.ativo && (
                      <button type="button" className="action-btn" title="Editar" onClick={() => openEdit(t)}>
                        <Pencil size={15} />
                      </button>
                    )}
                    {!t.ativo && (
                      <button type="button" className="action-btn" style={{ color: 'var(--success-color, #10b981)' }} title="Reativar" onClick={() => handleAtivar(t.id)}>
                        <RotateCcw size={15} />
                      </button>
                    )}
                    {t.ativo && (
                      <button type="button" className="action-btn action-btn-danger" style={{ color: 'var(--danger-color, #ef4444)' }} title="Desativar" onClick={() => requestDelete(t.id)}>
                        <PowerOff size={15} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {filtered.length === 0 && <div style={{ color: 'var(--text-muted)', fontSize: '.85rem' }}>Nenhum registro encontrado.</div>}
            </div>
          )}
          {viewMode === 'cards' && (
            <PaginationFooter
              totalItems={filtered.length}
              pageSize={pageSize}
            />
          )}
        </div>
      </div>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Editar dispositivo' : 'Novo dispositivo'}
        size={editing ? 'wide' : undefined}
        contentClassName={editing ? 'ac-dispositivo-modal ac-dispositivo-modal--edit' : 'ac-dispositivo-modal'}
        dialogId="modal-novo-dispositivo"
        footer={(
          <>
            <button type="button" className="btn-cancel" onClick={() => setModalOpen(false)}>Cancelar</button>
            <button
              type="button"
              className="btn-save"
              onClick={() => void handleSave()}
              disabled={create.isPending || update.isPending}
            >
              {(create.isPending || update.isPending) ? 'Salvando…' : 'Salvar'}
            </button>
          </>
        )}
      >
        <DispositivosFormModal form={form} setForm={setForm} editing={editing} />
      </Modal>

      <ConfirmModal
        open={!!deleteId}
        title="Desativar dispositivo"
        message="Desativar este dispositivo de leitura? Ele não poderá mais registrar acessos, mas seu histórico será mantido."
        confirmLabel="Desativar"
        onCancel={() => setDeleteId(null)}
        onConfirm={() => void confirmDelete()}
      />
    </div>
  );
}
